import { saveAs } from 'file-saver';
import React, { type RefObject, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { Tooltip } from 'react-tooltip';

import { ReactComponent as CopyLink } from '@assets/svg/copyLink-icon.svg';
import { ReactComponent as DownloadIcon } from '@assets/svg/header-download-icon.svg';
import { ReactComponent as CopyTextIcon } from '@assets/svg/ic-copyDetails.svg';
import { ReactComponent as PrintIcon } from '@assets/svg/print-icon.svg';

import LoaderGif from '@components/common/LoaderGif/LoaderGif';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import type { IActionItem } from '@model/actionItem';

import { useSaveActionItemDetailsMutation } from '@services/useSaveActionItemDetailsMutation';

import style from './style.module.scss';

interface IProps {
  id: string;
  actionItemData?: IActionItem;
  commentsInfo?: Array<string>;
  recommendationsInfo?: Array<string>;
  printRef: RefObject<HTMLDivElement>;
}

const DownloadAIDetailsMenu = ({
  id,
  actionItemData,
  commentsInfo,
  recommendationsInfo,
  printRef,
}: IProps) => {
  const { t } = useTranslation();
  const { actionItemId } = useParams();
  const [isPrint, setIsPrint] = useState(false);

  const { mutate: saveActionItemDetailsPdfMutate } = useSaveActionItemDetailsMutation();

  const downloadFailureCauseCategoriesInfoPdf = () => {
    showToast({ variant: 'success', message: t('The download will begin shortly.') });

    saveActionItemDetailsPdfMutate(actionItemId || '', {
      onSuccess: (data) => {
        saveAs(
          new Blob([data?.data], { type: 'text/plain;charset=utf-8' }),
          `AI Details ${actionItemData?.equipment_info}, ${actionItemData?.installation_point.name}.pdf`,
        );
      },
    });
  };

  const handleOnBeforeGetContent = () => {
    setIsPrint(true);
    return new Promise<void>((resolve) => setTimeout(resolve));
  };

  const handleAfterPrint = () => {
    setIsPrint(false);
  };

  const printParams = {
    onBeforePrint: handleOnBeforeGetContent,
    onAfterPrint: handleAfterPrint,
  };

  const onPrint = useReactToPrint({
    contentRef: printRef,
    ...printParams,
  });

  const handleCopyLink = () => {
    const url = window.location.href;
    try {
      navigator.clipboard.writeText(url || '').then(() => {
        showToast({ variant: 'success', message: t('Successfully copied to clipboard') });
      });
    } catch (e) {
      console.log(e);
    }
  };

  const handleCopyInfo = async () => {
    let copyInfo: string[] = [];

    if (actionItemData?.technicalInfo) {
      copyInfo = [...actionItemData.technicalInfo, ''];
    }

    if (recommendationsInfo?.length) {
      copyInfo.push(`${t('Short Description')}:`);
      copyInfo = [...copyInfo, ...recommendationsInfo];
    }

    if (actionItemData?.descriptionInfo.length) {
      copyInfo.push(`${t('Description')}:`);
      copyInfo.push(`${actionItemData?.descriptionInfo}`);
      copyInfo.push('');
    }

    if (commentsInfo?.length) {
      copyInfo.push(`${t('Comments')}:`);
      copyInfo = [...copyInfo, ...commentsInfo];
    }

    if (copyInfo.length) {
      const detailsInfo = copyInfo.join('\n');
      const blob = new Blob([detailsInfo], { type: 'text/plain' });
      const data = [new ClipboardItem({ ['text/plain']: blob })];

      await navigator.clipboard.write(data);
      showToast({ variant: 'success', message: t('Successfully copied to clipboard') });
    }
  };

  return (
    <Tooltip
      id={id}
      place="bottom"
      openOnClick
      className={style.aiDownloadEventMenu}
      clickable
      disableStyleInjection
      render={() => {
        return (
          <>
            <div className={style.aiDownloadEventItem} onClick={handleCopyInfo}>
              <CopyTextIcon className={style.copyTextIcon} />
              <span>{t('Copy all text')}</span>
            </div>
            <div className={style.aiDownloadEventItem} onClick={handleCopyLink}>
              <CopyLink className={style.copyLinkIcon} />
              <span>{t('Copy link')}</span>
            </div>
            <div className={style.aiDownloadEventItem} onClick={() => onPrint()}>
              <PrintIcon className={style.printIcon} />
              {isPrint && <LoaderGif className={style.loader} />}
              <span>{t('Print action item')}</span>
            </div>
            <div className={style.aiDownloadEventItem} onClick={downloadFailureCauseCategoriesInfoPdf}>
              <DownloadIcon className={style.downloadIcon} />
              <span>{t('Download Action Item')}</span>
            </div>
          </>
        );
      }}
    />
  );
};

export default DownloadAIDetailsMenu;
